import React, { memo } from 'react';
import { blockComponents } from './blockRegistry';
import { blockRendererAreEqual } from './blockRendererAreEqual';
import { blockWrapperClassName, isLayoutContainer } from './constants';
import BlockChrome from './components/BlockChrome';
import { useBlockDragDrop } from './hooks/useBlockDragDrop';
import { useBlockHandleMenu } from './hooks/useBlockHandleMenu';
import { useHandleVisibility } from './hooks/useHandleVisibility';

/**
 * BlockRenderer — maps block.type to a component and renders it in a wrapper
 * with a drag handle, drop indicator and selection.
 *
 * Recursive by design: container blocks (row, column, callout) render their
 * children through BlockRenderer again.
 *
 * The drop indicator is driven by ONE editor-level `dropTarget` — a block
 * draws a line only when it IS the target, so two lines can never appear.
 */

const BlockRendererInner = ({
  block,
  onUpdate,
  onInsertAfter,
  onUpdateAndInsert,
  onDelete,
  onMergeWithPrevious,
  onMergeWithNext,
  onNavigate,
  onEscapeContainer,
  onWrapInCallout,
  onUnwrapCallout,
  onOpenDoc,
  resolveDocMeta,
  availableDocs,
  snippetBacklinks,
  resolveVariable,
  availableVariables,
  onCreateVariable,
  hasVariablesBlock,
  docVariables,
  onDocVariablesChange,
  onRenameVariable,
  variablesHistoryEpoch,
  variableCampaign,
  allSprints,
  onCampaignVariablesChange,
  missionVariables,
  onMissionVariablesChange,
  onPatchInheritedVariable,
  variablesColumnWidths,
  onVariablesColumnWidthsChange,
  tickOverrides,
  onTickChange,
  fileOverrides,
  onFilesChange,
  missionId,
  docId,
  compact,
  slashExcludeIds,
  inTableCell,
  activeId,
  fileDragActive,
  dropTarget,
  onChildHoverChange,
  selectedBlockId,
  onSelect,
  multiSelectedIds,
  prevType,
  nextType,
  listNumber,
}) => {
  const { attributes, listeners, setDragRef, setDropRef, isDragging } = useBlockDragDrop(block);

  const {
    showHandle,
    childIsHovered,
    handleMouseEnter,
    handleMouseLeave,
    handleChildHover,
    showHandleForDragZone,
  } = useHandleVisibility({ onChildHoverChange });

  const {
    handleMenu,
    closeHandleMenu,
    handleHandleMouseDown,
    handleHandleMouseUp,
    handleHandleContextMenu,
    runConvertToCallout,
  } = useBlockHandleMenu({
    block,
    multiSelectedIds,
    onSelect,
    onWrapInCallout,
    activeId,
    isDragging,
  });

  const BlockComponent = blockComponents[block.type] || blockComponents.text;

  const combinedRef = (node) => {
    setDragRef(node);
    setDropRef(node);
  };

  const container = isLayoutContainer(block);
  const selected = selectedBlockId === block.id;
  const showDragAffordances =
    !activeId && !fileDragActive && !childIsHovered && !container;
  const indicatorZone =
    (activeId || fileDragActive) && dropTarget?.targetId === block.id
      ? dropTarget.zone
      : null;

  return (
    <BlockChrome
      block={block}
      className={blockWrapperClassName({
        block,
        inTableCell,
        selected,
        multiSelected: multiSelectedIds?.includes(block.id),
        isDragging,
        prevType,
        nextType,
      })}
      combinedRef={combinedRef}
      isDragging={isDragging}
      showDragAffordances={showDragAffordances}
      showHandle={showHandle || (selected && block.type === 'linked-variable-row')}
      listeners={listeners}
      attributes={attributes}
      showHandleForDragZone={showHandleForDragZone}
      handleMouseEnter={handleMouseEnter}
      handleMouseLeave={handleMouseLeave}
      handleHandleMouseDown={handleHandleMouseDown}
      handleHandleMouseUp={handleHandleMouseUp}
      handleHandleContextMenu={handleHandleContextMenu}
      handleMenu={handleMenu}
      closeHandleMenu={closeHandleMenu}
      runConvertToCallout={runConvertToCallout}
      container={container}
      snippetBacklinks={snippetBacklinks}
      resolveDocMeta={resolveDocMeta}
      onOpenDoc={onOpenDoc}
      indicatorZone={indicatorZone}
    >
      <BlockComponent
        block={block}
        listNumber={listNumber}
        onUpdate={onUpdate}
        onInsertAfter={onInsertAfter}
        onUpdateAndInsert={onUpdateAndInsert}
        onDelete={onDelete}
        onMergeWithPrevious={onMergeWithPrevious}
        onMergeWithNext={onMergeWithNext}
        onNavigate={onNavigate}
        onEscapeContainer={onEscapeContainer}
        onWrapInCallout={onWrapInCallout}
        onUnwrapCallout={onUnwrapCallout}
        onOpenDoc={onOpenDoc}
        resolveDocMeta={resolveDocMeta}
        availableDocs={availableDocs}
        snippetBacklinks={snippetBacklinks}
        resolveVariable={resolveVariable}
        availableVariables={availableVariables}
        onCreateVariable={onCreateVariable}
        hasVariablesBlock={hasVariablesBlock}
        docVariables={docVariables}
        onDocVariablesChange={onDocVariablesChange}
        onRenameVariable={onRenameVariable}
        variablesHistoryEpoch={variablesHistoryEpoch}
        variableCampaign={variableCampaign}
        allSprints={allSprints}
        onCampaignVariablesChange={onCampaignVariablesChange}
        missionVariables={missionVariables}
        onMissionVariablesChange={onMissionVariablesChange}
        onPatchInheritedVariable={onPatchInheritedVariable}
        variablesColumnWidths={variablesColumnWidths}
        onVariablesColumnWidthsChange={onVariablesColumnWidthsChange}
        tickOverrides={tickOverrides}
        onTickChange={onTickChange}
        fileOverrides={fileOverrides}
        onFilesChange={onFilesChange}
        missionId={missionId}
        docId={docId}
        compact={compact}
        slashExcludeIds={slashExcludeIds}
        activeId={activeId}
        fileDragActive={fileDragActive}
        dropTarget={dropTarget}
        onChildHoverChange={handleChildHover}
        selectedBlockId={selectedBlockId}
        onSelect={onSelect}
        multiSelectedIds={multiSelectedIds}
        dragListeners={
          block.type === 'image' || block.type === 'video' ? listeners : undefined
        }
        dragAttributes={
          block.type === 'image' || block.type === 'video' ? attributes : undefined
        }
      />
    </BlockChrome>
  );
};

export const BlockRenderer = memo(BlockRendererInner, blockRendererAreEqual);
